import {
	FC,
	useState,
	useEffect,
	createContext,
	useContext,
	useRef,
	Dispatch,
	SetStateAction,
	MutableRefObject,
} from "react";

import { useApi } from "@/hooks/useApi";
import { transformData } from "@/utils/transformData";
import { authMap } from "@/utils/mappings/auth";
import { AuthModel } from "./_models";
import { WithChildren } from "@/../_metronic/helpers";
import * as authHelper from "./AuthHelpers";

import { LayoutSplashScreen } from "@/../_metronic/layout/core";

type TimerIntervalIdRef = MutableRefObject<NodeJS.Timer | null>;

type AuthContextProps = {
	auth: AuthModel | undefined;
	timer: number;
	setTimer: (timer: number) => void;
	timerIntervalId: TimerIntervalIdRef;
	saveAuth: (auth: AuthModel | undefined) => void;
	currentAuth: Auth | undefined;
	setCurrentAuth: Dispatch<SetStateAction<Auth | undefined>>;
	logout: () => void;
};

const initAuthContextPropsState = {
	auth: authHelper.getAuth(),
	timer: 0,
	setTimer: () => {},
	timerIntervalId: { current: null },
	saveAuth: () => {},
	currentAuth: undefined,
	setCurrentAuth: () => {},
	logout: () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
	return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
	const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
	const [currentAuth, setCurrentAuth] = useState<Auth | undefined>();
	const [timer, setTimer] = useState(0);
	const timerIntervalId = useRef<NodeJS.Timer | null>(null);

	const saveAuth = (auth: AuthModel | undefined) => {
		setAuth(auth);
		if (auth) {
			authHelper.setAuth(auth);
		} else {
			authHelper.removeAuth();
		}
	};

	const logout = () => {
		saveAuth(undefined);
		setCurrentAuth(undefined);
	};

	useEffect(() => {
		const MAX_IDLE_TIME = 1800;
		if (timer === MAX_IDLE_TIME) {
			if (timerIntervalId.current) {
				clearInterval(timerIntervalId.current);
			}

			logout();
			return;
		}

		if (currentAuth) {
			timerIntervalId.current = setTimeout(() => {
				setTimer(timer + 1);
			}, 1000);
		}
		// eslint-disable-next-line
	}, [timer, currentAuth]);

	if (currentAuth?.user && currentAuth?.permissions) {
		currentAuth.user.can = (permissionName) => {
			const isAdmin = currentAuth.user.profiles.some(
				(profile) => profile.name === "Administrador"
			);
			const hasPermission = currentAuth.permissions.some(
				(permission) => permission.name === permissionName
			);

			return isAdmin || hasPermission;
		};
	}

	return (
		<AuthContext.Provider
			value={{
				auth,
				timer,
				setTimer,
				timerIntervalId,
				saveAuth,
				currentAuth,
				setCurrentAuth,
				logout,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

const AuthInit: FC<WithChildren> = ({ children }) => {
	const { auth, logout, currentAuth, setCurrentAuth } = useAuth();
	const { getUserByToken } = useApi();

	const didRequest = useRef(false);
	const [showSplashScreen, setShowSplashScreen] = useState(true);

	// We should request user by authToken (IN OUR EXAMPLE IT'S access_token) before rendering the application
	useEffect(() => {
		const requestUser = async () => {
			try {
				if (!didRequest.current && !currentAuth) {
					const response = await getUserByToken();
					if (response?.data) {
						const transformedData = transformData<Auth>(response.data, authMap);
						setCurrentAuth(transformedData);
					}
				}
			} catch (error) {
				console.error(error);
				if (!didRequest.current) {
					logout();
				}
			} finally {
				setShowSplashScreen(false);
			}

			return () => (didRequest.current = true);
		};

		if (auth && auth.access_token) {
			requestUser();
		} else {
			logout();
			setShowSplashScreen(false);
		}

		// eslint-disable-next-line
	}, [auth]);

	return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>;
};

export { AuthProvider, AuthInit, useAuth };
